/**
 * Feb 2026 · /app/enquiries — admin console for the Enquiries module.
 * Two tabs: Inbox (list, filter, detail dialog with status/note/assign)
 * and Types (CRUD types + subtypes + status pipeline).
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Inbox, Settings, Plus, Copy, ExternalLink, Trash2, Code, Palette, Upload as UploadIcon, Users } from "lucide-react";
import { useAuth } from "@/lib/auth";

const STATUS_COLORS = {
  new: "bg-blue-50 text-blue-800 border-blue-300",
  contacted: "bg-amber-50 text-amber-800 border-amber-300",
  qualified: "bg-violet-50 text-violet-800 border-violet-300",
  quoted: "bg-cyan-50 text-cyan-800 border-cyan-300",
  won: "bg-emerald-50 text-emerald-800 border-emerald-300",
  lost: "bg-rose-50 text-rose-800 border-rose-300",
};
const statusChip = (s) => STATUS_COLORS[s] || "bg-slate-100 text-slate-700 border-slate-300";

export default function EnquiriesPage() {
  const { activeWorkspaceId: ws } = useAuth();
  const [tab, setTab] = useState("inbox");
  return (
    <div className="p-4 sm:p-6 md:px-10" data-testid="enquiries-page">
      <div className="mb-5">
        <div className="text-[10px] uppercase tracking-[0.25em] text-amber-700/80 font-semibold mb-1">CRM</div>
        <h1 className="text-3xl font-bold" style={{ fontFamily: `"Cormorant Garamond", Georgia, serif`, fontWeight: 500, letterSpacing: "-0.02em" }}>Enquiries</h1>
        <p className="text-xs text-muted-foreground mt-1">Intake pipeline — widget · dedicated page · manual · auto from studio booking</p>
      </div>
      <div className="flex gap-1 mb-4 border-b border-border" data-testid="enq-tabs">
        {[
          { k: "inbox", label: "Inbox", icon: Inbox },
          { k: "types", label: "Types", icon: Settings },
          { k: "widgets", label: "Widgets", icon: Code },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
                  data-testid={`enq-tab-${t.k}`}
                  className={`px-3 py-2 text-xs uppercase tracking-widest font-semibold transition-colors flex items-center gap-1.5 border-b-2 -mb-px ${
                    tab === t.k ? "text-primary border-primary" : "text-muted-foreground border-transparent hover:text-foreground"}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>
      {ws && tab === "inbox" && <Inboxx ws={ws} />}
      {ws && tab === "types" && <Types ws={ws} />}
      {ws && tab === "widgets" && <Widgets ws={ws} />}
    </div>
  );
}

function Inboxx({ ws }) {
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [members, setMembers] = useState([]);
  const [filter, setFilter] = useState({ status: "", type_id: "", q: "", assigned_to: "" });
  const [inboxView, setInboxView] = useState("list");  // "list" | "kanban"
  const [dragId, setDragId] = useState(null);
  const [focus, setFocus] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const p = new URLSearchParams();
      if (filter.status) p.set("status", filter.status);
      if (filter.type_id) p.set("type_id", filter.type_id);
      if (filter.q) p.set("q", filter.q);
      const [r, t, m] = await Promise.all([
        api.get(`/workspaces/${ws}/enquiries?${p.toString()}`),
        api.get(`/workspaces/${ws}/enquiries/types`),
        api.get(`/workspaces/${ws}/members`).catch(() => ({ data: [] })),
      ]);
      setRows(r.data.enquiries || []);
      setTypes(t.data.types || []);
      setMembers(Array.isArray(m.data) ? m.data : []);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
  };
  useEffect(() => { if (ws) load(); /* eslint-disable-next-line */ }, [ws, filter.status, filter.type_id]);

  // Assignee filter is client-side (backend does not paginate on it)
  const filteredRows = useMemo(() => {
    if (!filter.assigned_to) return rows;
    if (filter.assigned_to === "__unassigned") return rows.filter((r) => !r.assigned_to);
    return rows.filter((r) => r.assigned_to === filter.assigned_to);
  }, [rows, filter.assigned_to]);

  const memberName = (uid) => {
    const m = members.find((x) => (x.user_id || x.id) === uid);
    return m?.name || m?.email || uid?.slice(0, 6);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Input placeholder="Search name / email / code" value={filter.q}
               onChange={(e) => setFilter({ ...filter, q: e.target.value })}
               onKeyDown={(e) => { if (e.key === "Enter") load(); }}
               className="rounded-none h-8 max-w-xs" data-testid="enq-search" />
        <select value={filter.status}
                onChange={(e) => setFilter({ ...filter, status: e.target.value })}
                className="h-8 border border-border bg-background text-xs rounded-none px-2"
                data-testid="enq-filter-status">
          <option value="">All statuses</option>
          {["new", "contacted", "qualified", "quoted", "won", "lost"].map((s) =>
            <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filter.type_id}
                onChange={(e) => setFilter({ ...filter, type_id: e.target.value })}
                className="h-8 border border-border bg-background text-xs rounded-none px-2"
                data-testid="enq-filter-type">
          <option value="">All types</option>
          {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select value={filter.assigned_to}
                onChange={(e) => setFilter({ ...filter, assigned_to: e.target.value })}
                className="h-8 border border-border bg-background text-xs rounded-none px-2"
                data-testid="enq-filter-assignee">
          <option value="">All assignees</option>
          <option value="__unassigned">Unassigned</option>
          {members.map((m) => {
            const uid = m.user_id || m.id;
            return <option key={uid} value={uid}>{m.name || m.email}</option>;
          })}
        </select>
        <div className="inline-flex border border-border ml-auto" data-testid="enq-view-toggle">
          {["list", "kanban"].map((v) => (
            <button key={v} onClick={() => setInboxView(v)}
                    data-testid={`enq-view-${v}`}
                    className={`px-2.5 py-1 text-[11px] uppercase tracking-widest font-semibold transition-colors ${
                      inboxView === v ? "bg-primary text-primary-foreground" : "bg-transparent text-muted-foreground hover:text-foreground"}`}>
              {v}
            </button>
          ))}
        </div>
        <Button size="sm" className="rounded-none" onClick={() => setCreating(true)}
                data-testid="enq-new-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> New enquiry
        </Button>
      </div>
      {inboxView === "kanban" ? (
        <EnquiryKanban ws={ws} rows={filteredRows} types={types} members={members}
                          dragId={dragId} setDragId={setDragId}
                          onOpen={setFocus} onMoved={load} />
      ) : (
      <div className="border border-border overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-muted/50">
            <tr>
              {["Code", "Customer", "Type", "Subtype", "Source", "Status", "Assigned", "SLA", "Created"].map((h) =>
                <th key={h} className="text-left px-2 py-1.5 font-semibold uppercase tracking-widest text-[10px] text-muted-foreground whitespace-nowrap">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((e) => (
              <tr key={e.id} onClick={() => setFocus(e)}
                  className="border-t border-border hover:bg-muted/30 cursor-pointer"
                  data-testid={`enq-row-${e.id}`}>
                <td className="px-2 py-1.5 font-mono">{e.code}</td>
                <td className="px-2 py-1.5">
                  <div className="truncate max-w-[160px] font-semibold">{e.customer_name}</div>
                  <div className="text-[10px] text-muted-foreground truncate max-w-[160px]">{e.customer_email}</div>
                </td>
                <td className="px-2 py-1.5">{e.type_name}</td>
                <td className="px-2 py-1.5 text-[11px]">{e.subtype_slug || "—"}</td>
                <td className="px-2 py-1.5 text-[11px]"><span className="bg-muted/50 px-1.5 py-0.5">{e.source}</span></td>
                <td className="px-2 py-1.5"><span className={`text-[10px] px-1.5 py-0.5 border ${statusChip(e.status)}`}>{e.status}</span></td>
                <td className="px-2 py-1.5 text-[11px]">{e.assigned_to ? memberName(e.assigned_to) : "—"}</td>
                <td className="px-2 py-1.5"><SlaChip enquiry={e} /></td>
                <td className="px-2 py-1.5 text-[11px] whitespace-nowrap">{e.created_at?.slice(0, 16).replace("T", " ")}</td>
              </tr>
            ))}
            {filteredRows.length === 0 && (
              <tr><td colSpan={9} className="text-center py-6 text-muted-foreground italic">
                No enquiries yet. Share your <code>/enquire/{ws}</code> link or embed the widget on your site.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      )}
      {focus && <EnquiryDetail ws={ws} enquiry={focus}
                                    types={types} members={members}
                                    onClose={() => setFocus(null)}
                                    onSaved={() => { load(); setFocus(null); }} />}
      {creating && <NewEnquiryDialog ws={ws} types={types} members={members}
                                        onClose={() => setCreating(false)}
                                        onCreated={() => { setCreating(false); load(); }} />}
    </div>
  );
}

// Feb 2026 · Kanban view — one column per status (from the default
// pipeline). Cards are HTML5 draggable; drop onto a column PATCHes the
// enquiry's status. Uses the same statusChip palette as the list view.
function EnquiryKanban({ ws, rows, types, members, dragId, setDragId, onOpen, onMoved }) {
  const STATUSES = ["new", "contacted", "qualified", "quoted", "won", "lost"];
  const [dropCol, setDropCol] = useState(null);
  const grouped = STATUSES.reduce((acc, s) => (acc[s] = [], acc), {});
  for (const r of rows) {
    (grouped[r.status] || (grouped[r.status] = [])).push(r);
  }
  const memberName = (uid) => {
    const m = members.find((x) => (x.user_id || x.id) === uid);
    return m?.name || m?.email || "";
  };
  const typeName = (id) => types.find((t) => t.id === id)?.name || "";
  const onDrop = async (status) => {
    if (!dragId) return;
    try {
      await api.patch(`/workspaces/${ws}/enquiries/${dragId}`, { status });
      toast.success(`Moved to ${status}`);
      onMoved();
    } catch (e) { toast.error(apiErrorMessage(e, "Move failed")); }
    finally { setDragId(null); setDropCol(null); }
  };
  return (
    <div className="flex gap-3 overflow-x-auto pb-2" data-testid="enq-kanban">
      {STATUSES.map((s) => {
        const isTarget = dropCol === s;
        return (
          <div key={s} className={`flex-shrink-0 w-60 border ${isTarget ? "border-primary shadow-md" : "border-border"} bg-muted/20`}
               data-testid={`enq-kanban-col-${s}`}
               onDragOver={(e) => { e.preventDefault(); setDropCol(s); }}
               onDragLeave={() => setDropCol((c) => c === s ? null : c)}
               onDrop={(e) => { e.preventDefault(); onDrop(s); }}>
            <div className={`px-2 py-1.5 text-[10px] uppercase tracking-widest font-bold border-b border-border flex items-center justify-between ${statusChip(s)}`}>
              <span>{s}</span>
              <span className="font-mono">{grouped[s].length}</span>
            </div>
            <div className="p-2 space-y-1.5 min-h-[120px]">
              {grouped[s].map((e) => (
                <div key={e.id}
                     draggable
                     onDragStart={() => setDragId(e.id)}
                     onDragEnd={() => setDragId(null)}
                     onClick={() => onOpen(e)}
                     data-testid={`enq-kanban-card-${e.id}`}
                     className={`p-2 bg-white border border-border cursor-pointer hover:border-primary text-[11px] ${dragId === e.id ? "opacity-40" : ""}`}>
                  <div className="flex items-center justify-between gap-1 mb-1">
                    <span className="font-mono text-[10px]">{e.code}</span>
                    <span className="text-[9px] text-muted-foreground uppercase tracking-widest truncate">{typeName(e.type_id)}</span>
                  </div>
                  <div className="font-semibold truncate">{e.customer_name}</div>
                  <div className="text-[10px] text-muted-foreground truncate">{e.customer_email}</div>
                  {e.linked_services?.length > 0 && (
                    <div className="mt-1 text-[9px] text-amber-800 truncate">🎬 {e.linked_services.slice(0, 2).join(" · ")}</div>
                  )}
                  <div className="mt-1 flex items-center justify-between text-[9px] text-muted-foreground">
                    <span>{e.assigned_to ? memberName(e.assigned_to).split(" ")[0] : "—"}</span>
                    <span>{e.created_at?.slice(5, 10)}</span>
                  </div>
                </div>
              ))}
              {grouped[s].length === 0 && (
                <div className="text-[10px] text-muted-foreground italic p-2 text-center">Drop cards here</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}


function EnquiryDetail({ ws, enquiry, types, members, onClose, onSaved }) {
  const [form, setForm] = useState({
    status: enquiry.status,
    subtype_slug: enquiry.subtype_slug || "",
    assigned_to: enquiry.assigned_to || "",
    note: "",
  });
  const [busy, setBusy] = useState(false);
  const [matches, setMatches] = useState({ leads: [], open_enquiries: [] });
  const type = types.find((t) => t.id === enquiry.type_id) || {};
  const statuses = type.statuses || ["new", "contacted", "qualified", "quoted", "won", "lost"];

  useEffect(() => {
    const email = (enquiry.customer_email || "").trim();
    const phone = (enquiry.customer_phone || "").trim();
    if (!email && !phone) return;
    (async () => {
      try {
        const p = new URLSearchParams();
        if (email) p.set("email", email);
        if (phone) p.set("phone", phone);
        p.set("exclude_enquiry_id", enquiry.id);
        const r = await api.get(`/workspaces/${ws}/enquiries/match?${p.toString()}`);
        setMatches(r.data || { leads: [], open_enquiries: [] });
      } catch { /* silent */ }
    })();
  }, [ws, enquiry.id, enquiry.customer_email, enquiry.customer_phone]);

  const linkLead = async (leadId) => {
    try {
      // Send empty string when unlinking — backend's patch skips explicit
      // null but writes "" through so the lead_id gets cleared.
      await api.patch(`/workspaces/${ws}/enquiries/${enquiry.id}`, { lead_id: leadId || "" });
      toast.success(leadId ? "Lead linked" : "Lead unlinked");
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Link failed")); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const patch = {};
      if (form.status !== enquiry.status) patch.status = form.status;
      if (form.subtype_slug !== (enquiry.subtype_slug || "")) patch.subtype_slug = form.subtype_slug;
      if (form.assigned_to !== (enquiry.assigned_to || "")) patch.assigned_to = form.assigned_to;
      if (form.note?.trim()) patch.note = form.note.trim();
      if (Object.keys(patch).length === 0) { onClose(); return; }
      await api.patch(`/workspaces/${ws}/enquiries/${enquiry.id}`, patch);
      toast.success("Enquiry updated");
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-none max-w-2xl w-[calc(100vw-1rem)] max-h-[92vh] overflow-y-auto p-0" data-testid="enq-detail-dialog">
        <DialogHeader className="p-4 border-b border-border sticky top-0 bg-background z-10">
          <DialogTitle className="flex items-center gap-2">
            <span className="font-mono">{enquiry.code}</span>
            <span className={`text-[10px] px-1.5 py-0.5 border ${statusChip(enquiry.status)}`}>{enquiry.status}</span>
            <span className="text-xs text-muted-foreground ml-auto">{enquiry.type_name}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="p-4 space-y-4">
          <section className="border border-border p-3 bg-muted/20">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Customer</div>
                <div className="text-sm font-bold">{enquiry.customer_name}</div>
                <div className="text-xs text-muted-foreground">{enquiry.customer_email} · {enquiry.customer_phone}</div>
                {enquiry.company && <div className="text-xs">{enquiry.company}</div>}
                {enquiry.budget && <div className="text-xs mt-1">Budget: <b>{enquiry.budget}</b></div>}
                {enquiry.lead_id && (
                  <div className="text-[10px] text-primary mt-2 font-semibold flex items-center gap-2" data-testid="enq-detail-lead-chip">
                    → Linked to lead {enquiry.lead_id.slice(0, 8)}
                    <button onClick={() => linkLead(null)}
                             className="text-[10px] uppercase tracking-widest text-rose-700 underline"
                             data-testid="enq-detail-lead-unlink">
                      Unlink
                    </button>
                  </div>
                )}
              </div>
              {!enquiry.lead_id && (
                <Button size="sm" variant="outline" className="rounded-none text-[10px] uppercase tracking-widest"
                        onClick={async () => {
                          try {
                            await api.put(`/workspaces/${ws}/enquiries/${enquiry.id}/convert-to-lead`);
                            toast.success("Lead created and linked");
                            onSaved();
                          } catch (e) { toast.error(apiErrorMessage(e, "Convert failed")); }
                        }}
                        data-testid="enq-detail-convert-lead">
                  Convert to lead
                </Button>
              )}
            </div>
          </section>
          {(matches.leads?.length > 0 || matches.open_enquiries?.length > 0) && (
            <section data-testid="enq-detail-matches">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">
                Matches on email / phone
              </div>
              {matches.open_enquiries?.length > 0 && (
                <div className="border border-amber-400 bg-amber-50 p-2.5 mb-2" data-testid="enq-detail-dupe">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-amber-900 mb-1">
                    ⚠ {matches.open_enquiries.length} other open enquir{matches.open_enquiries.length === 1 ? "y" : "ies"} for this customer
                  </div>
                  <ul className="text-[11px] space-y-1 max-h-24 overflow-y-auto">
                    {matches.open_enquiries.map((e) => (
                      <li key={e.id} className="flex items-center gap-2">
                        <span className="font-mono text-[10px] bg-white border border-amber-300 px-1">{e.code}</span>
                        <span className="truncate flex-1">{e.customer_name} · {e.type_name}</span>
                        <span className="text-[10px] text-amber-800 uppercase tracking-widest">{e.status}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {!enquiry.lead_id && matches.leads?.length > 0 && (
                <div className="border border-emerald-300 bg-emerald-50/60 p-2.5" data-testid="enq-detail-link-leads">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-900 mb-1">
                    Link an existing lead
                  </div>
                  <div className="space-y-1">
                    {matches.leads.map((l) => (
                      <div key={l.id} className="flex items-center gap-2 p-1.5 bg-white border border-emerald-200 text-[11px]"
                           data-testid={`enq-detail-link-lead-${l.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold truncate">{l.name}</div>
                          <div className="text-[10px] text-muted-foreground truncate">
                            {l.email || "—"} · {l.phone || "—"}
                          </div>
                        </div>
                        <span className="text-[10px] font-mono bg-emerald-100 border border-emerald-300 px-1 whitespace-nowrap">
                          {l.enquiry_count || 0} enq
                        </span>
                        <button onClick={() => linkLead(l.id)}
                                 className="text-[10px] uppercase tracking-widest text-emerald-900 border border-emerald-600 px-2 py-1 hover:bg-emerald-100"
                                 data-testid={`enq-detail-link-lead-btn-${l.id}`}>
                          Link
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}
          {enquiry.message && (
            <section>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Message</div>
              <div className="text-sm whitespace-pre-wrap border border-border p-3 bg-white">{enquiry.message}</div>
            </section>
          )}
          {enquiry.linked_services?.length > 0 && (
            <section>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Requested services</div>
              <div className="flex flex-wrap gap-1">
                {enquiry.linked_services.map((s, i) => (
                  <span key={i} className="text-[11px] bg-amber-50 border border-amber-300 px-2 py-0.5">{s}</span>
                ))}
              </div>
            </section>
          )}
          {/* Status pipeline as clickable pills — click to advance */}
          <section>
            <Label className="text-xs">Status pipeline</Label>
            <div className="flex flex-wrap gap-1.5 mt-1" data-testid="enq-detail-status">
              {statuses.map((s) => {
                const active = form.status === s;
                return (
                  <button key={s} type="button"
                          onClick={() => setForm({ ...form, status: s })}
                          data-testid={`enq-detail-status-${s}`}
                          className={`text-[10px] uppercase tracking-widest px-2.5 py-1.5 border transition-colors ${
                            active
                              ? "bg-primary text-primary-foreground border-primary"
                              : "bg-white text-muted-foreground border-border hover:border-primary/40 hover:text-foreground"
                          }`}>
                    {s}
                  </button>
                );
              })}
            </div>
          </section>
          <section className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Subtype</Label>
              <select value={form.subtype_slug}
                      onChange={(e) => setForm({ ...form, subtype_slug: e.target.value })}
                      className="w-full h-9 border border-border bg-background text-xs rounded-none px-2"
                      data-testid="enq-detail-subtype">
                <option value="">—</option>
                {(type.subtypes || []).map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Assigned to</Label>
              <select value={form.assigned_to}
                      onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}
                      className="w-full h-9 border border-border bg-background text-xs rounded-none px-2"
                      data-testid="enq-detail-assign">
                <option value="">Unassigned</option>
                {members.map((m) => {
                  const uid = m.user_id || m.id;
                  return <option key={uid} value={uid}>{m.name || m.email}</option>;
                })}
              </select>
            </div>
          </section>
          <section>
            <Label className="text-xs">Add a note</Label>
            <Textarea rows={2} value={form.note}
                       onChange={(e) => setForm({ ...form, note: e.target.value })}
                       placeholder="Called, will follow up tomorrow at 4pm…"
                       className="rounded-none text-xs" data-testid="enq-detail-note" />
          </section>
          {enquiry.activity?.length > 0 && (
            <section>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Activity</div>
              <div className="space-y-1 max-h-48 overflow-y-auto border border-border p-2 bg-white">
                {[...enquiry.activity].reverse().map((a, i) => (
                  <div key={i} className="text-[11px] border-b border-border/50 pb-1">
                    <span className="text-muted-foreground font-mono text-[10px]">{a.at?.slice(0, 16).replace("T", " ")}</span>
                    <span className="ml-2">{a.note}</span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
        <DialogFooter className="p-4 border-t border-border sticky bottom-0 bg-background">
          <Button variant="outline" className="rounded-none" onClick={onClose}>Close</Button>
          <Button className="rounded-none" onClick={save} disabled={busy} data-testid="enq-detail-save">
            {busy ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Types({ ws }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  // Feb 2026 · Fetch the workspace's booking_slug so copied enquiry
  // links use the friendly host if one is configured — e.g.
  // /enquire/acmestudio/coporate-event instead of /enquire/<uuid>/…
  const [bookingSlug, setBookingSlug] = useState("");
  const load = async () => {
    try {
      const r = await api.get(`/workspaces/${ws}/enquiries/types`);
      setRows(r.data.types || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
  };
  const loadSlug = async () => {
    try {
      const r = await api.get(`/workspaces/${ws}/studio/widget-config`);
      setBookingSlug(r.data?.widget_config?.booking_slug || "");
    } catch { /* silent — slug is optional */ }
  };
  useEffect(() => { if (ws) { load(); loadSlug(); } /* eslint-disable-next-line */ }, [ws]);

  const del = async (t) => {
    if (!window.confirm(`Delete type "${t.name}"? Enquiries referencing this type will block the delete.`)) return;
    try {
      await api.delete(`/workspaces/${ws}/enquiries/types/${t.id}`);
      toast.success("Type deleted");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };
  const publicHandle = bookingSlug || ws;
  const copyLink = (t) => {
    const url = `${window.location.origin}/enquire/${publicHandle}/${t.slug}`;
    navigator.clipboard.writeText(url).then(
      () => toast.success(bookingSlug ? "Friendly link copied" : "Public link copied"),
      () => toast.info(url),
    );
  };

  return (
    <div data-testid="enq-types">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-sm font-bold">Enquiry types</h2>
          <p className="text-[11px] text-muted-foreground">Configure the intake categories, subtypes and status pipeline.</p>
          {bookingSlug ? (
            <p className="text-[10px] text-emerald-700 mt-1" data-testid="enq-types-friendly-host">
              Public URLs use <code className="font-mono">/enquire/{bookingSlug}/…</code>
            </p>
          ) : (
            <p className="text-[10px] text-muted-foreground mt-1" data-testid="enq-types-friendly-host-hint">
              Tip · set a workspace slug in <b>Studio Booking → Widget Settings</b> to replace <code className="font-mono">/enquire/{"<uuid>"}/…</code> with a friendly URL.
            </p>
          )}
        </div>
        <Button size="sm" className="rounded-none" onClick={() => setEditing("new")} data-testid="enq-types-new">
          <Plus className="w-3.5 h-3.5 mr-1" /> New type
        </Button>
      </div>
      <div className="grid gap-2">
        {rows.map((t) => (
          <div key={t.id} className="border border-border p-3 flex flex-wrap items-start gap-3 bg-white"
               data-testid={`enq-type-${t.slug}`}>
            <div className="flex-1 min-w-[240px]">
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold">{t.name}</span>
                <span className="text-[10px] font-mono text-muted-foreground bg-muted/50 px-1">/{t.slug}</span>
                {!t.active && <span className="text-[10px] uppercase tracking-widest text-neutral-500 border border-neutral-300 px-1">paused</span>}
              </div>
              <div className="text-[11px] text-muted-foreground mt-1">
                {t.subtypes?.length || 0} subtype{t.subtypes?.length !== 1 ? "s" : ""} · {t.statuses?.length || 0} status{t.statuses?.length !== 1 ? "es" : ""}
              </div>
              {t.subtypes?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {t.subtypes.map((s) => (
                    <span key={s.slug} className="text-[10px] bg-muted/40 px-1.5 py-0.5 border border-border">{s.name}</span>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => copyLink(t)} title="Copy public link"
                      className="text-[10px] uppercase tracking-widest font-semibold text-primary border border-primary/40 px-2 py-1 hover:bg-primary/5 inline-flex items-center gap-1"
                      data-testid={`enq-type-link-${t.slug}`}>
                <Copy className="w-3 h-3" /> Link
              </button>
              <a href={`/enquire/${publicHandle}/${t.slug}`} target="_blank" rel="noopener noreferrer"
                 title="Open public page"
                 className="text-[10px] uppercase tracking-widest font-semibold text-primary border border-primary/40 px-2 py-1 hover:bg-primary/5 inline-flex items-center gap-1"
                 data-testid={`enq-type-open-${t.slug}`}>
                <ExternalLink className="w-3 h-3" />
              </a>
              <button onClick={() => setEditing(t)}
                      className="text-[10px] uppercase tracking-widest font-semibold text-foreground border border-border px-2 py-1 hover:bg-muted/40"
                      data-testid={`enq-type-edit-${t.slug}`}>Edit</button>
              <button onClick={() => del(t)}
                      className="text-[10px] uppercase tracking-widest font-semibold text-rose-700 border border-rose-300 px-2 py-1 hover:bg-rose-50 inline-flex items-center gap-1"
                      data-testid={`enq-type-delete-${t.slug}`}>
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
      </div>
      {editing && <TypeDialog ws={ws} type={editing === "new" ? null : editing}
                                 onClose={() => setEditing(null)}
                                 onDone={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function TypeDialog({ ws, type, onClose, onDone }) {
  const [form, setForm] = useState({
    name: type?.name || "",
    slug: type?.slug || "",
    icon: type?.icon || "",
    subtypes: [...(type?.subtypes || [])],
    statuses: [...(type?.statuses || ["new", "contacted", "qualified", "quoted", "won", "lost"])],
    active: type?.active !== false,
    sort_order: type?.sort_order || 0,
    auto_reply: type?.auto_reply || { enabled: false, email_subject: "", email_html: "", whatsapp_text: "" },
    page_config: type?.page_config || {
      hero_image_url: "", logo_url: "", tagline: "", welcome_html: "",
      thank_you_html: "", terms_html: "", cta_label: "Submit enquiry",
      primary_color: "#B8860B", accent_color: "#241A11",
      background_color: "#FBF7F0", text_color: "#241A11",
      font_family: "serif",
      require_phone: false, require_company: false, require_budget: false,
      require_message: true, show_workspace_name: true, show_powered_by: true,
    },
    // Feb 2026 · R268ooo · inherit (default) uses workspace master style;
    // override lets this type customize while falling back to master.
    page_config_mode: type?.page_config_mode || "inherit",
    // Feb 2026 · R280.8 · Assignment auto-rule shortlist.
    auto_assign_pool: [...(type?.auto_assign_pool || [])],
    auto_assign_mode: type?.auto_assign_mode || "round_robin",
  });
  const [busy, setBusy] = useState(false);
  const [subDraft, setSubDraft] = useState("");
  const [statusDraft, setStatusDraft] = useState("");
  const [showPageConfig, setShowPageConfig] = useState(false);
  const isNew = !type;
  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const body = { ...form };
      if (isNew) await api.post(`/workspaces/${ws}/enquiries/types`, body);
      else await api.patch(`/workspaces/${ws}/enquiries/types/${type.id}`, body);
      toast.success(isNew ? "Type created" : "Type updated");
      onDone();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-none max-w-2xl w-[calc(100vw-1rem)] max-h-[92vh] overflow-y-auto p-0" data-testid="enq-type-dialog">
        <DialogHeader className="p-4 border-b border-border sticky top-0 bg-background z-10">
          <DialogTitle>{isNew ? "New enquiry type" : "Edit enquiry type"}</DialogTitle>
        </DialogHeader>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none h-9" data-testid="enq-type-name" />
            </div>
            <div>
              <Label className="text-xs">Slug</Label>
              <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="Auto from name" className="rounded-none h-9 font-mono" data-testid="enq-type-slug" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Subtypes</Label>
            <div className="space-y-1 mt-1">
              {form.subtypes.map((s, i) => (
                <div key={i} className="flex items-center gap-1">
                  <Input value={s.name}
                          onChange={(e) => {
                            const arr = [...form.subtypes];
                            arr[i] = { ...arr[i], name: e.target.value };
                            setForm({ ...form, subtypes: arr });
                          }}
                          className="rounded-none h-8" />
                  <button onClick={() => setForm({ ...form, subtypes: form.subtypes.filter((_, j) => j !== i) })}
                          className="text-rose-600 px-2"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              <div className="flex items-center gap-1">
                <Input value={subDraft} onChange={(e) => setSubDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && subDraft.trim()) {
                            e.preventDefault();
                            setForm({ ...form, subtypes: [...form.subtypes, { name: subDraft.trim() }] });
                            setSubDraft("");
                          }
                        }}
                        placeholder="Type + Enter to add subtype"
                        className="rounded-none h-8" data-testid="enq-type-sub-add" />
              </div>
            </div>
          </div>
          <div>
            <Label className="text-xs">Status pipeline (drag/reorder in a future iteration)</Label>
            <div className="flex flex-wrap gap-1 mt-1">
              {form.statuses.map((s, i) => (
                <span key={i} className={`text-[11px] px-2 py-1 border ${statusChip(s)} inline-flex items-center gap-1`}>
                  {s}
                  <button onClick={() => setForm({ ...form, statuses: form.statuses.filter((_, j) => j !== i) })}
                          className="text-current opacity-60 hover:opacity-100">×</button>
                </span>
              ))}
            </div>
            <div className="flex items-center gap-1 mt-1">
              <Input value={statusDraft} onChange={(e) => setStatusDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && statusDraft.trim()) {
                          e.preventDefault();
                          setForm({ ...form, statuses: [...form.statuses, statusDraft.trim().toLowerCase()] });
                          setStatusDraft("");
                        }
                      }}
                      placeholder="Type + Enter to add status"
                      className="rounded-none h-8" data-testid="enq-type-status-add" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={form.active}
                    onChange={(e) => setForm({ ...form, active: e.target.checked })}
                    data-testid="enq-type-active" />
            Active (hides from public widget when off)
          </label>
          {/* Feb 2026 · Auto-reply block — instant email/WhatsApp
              acknowledgement fired when a public enquiry lands on this
              type. Supports {{name}}, {{code}}, {{studio}}, {{type}}. */}
          <div className="border border-border p-3 bg-gradient-to-br from-amber-50/40 to-white">
            <label className="flex items-center gap-2 text-xs font-semibold mb-2" data-testid="enq-type-autoreply-block">
              <input type="checkbox" checked={form.auto_reply.enabled}
                     onChange={(e) => setForm({ ...form, auto_reply: { ...form.auto_reply, enabled: e.target.checked } })}
                     data-testid="enq-type-autoreply-enabled" />
              <span className="uppercase tracking-widest text-[10px] text-amber-800">Auto-reply to customer</span>
            </label>
            {form.auto_reply.enabled && (
              <div className="space-y-2">
                <div className="text-[10px] text-muted-foreground">
                  Fired instantly when a public enquiry lands. Placeholders: <code>{"{{name}}"}</code> <code>{"{{code}}"}</code> <code>{"{{studio}}"}</code> <code>{"{{type}}"}</code>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest">Email subject</Label>
                  <Input value={form.auto_reply.email_subject}
                         onChange={(e) => setForm({ ...form, auto_reply: { ...form.auto_reply, email_subject: e.target.value } })}
                         placeholder="Thanks {{name}} — we got your enquiry {{code}}"
                         className="rounded-none h-9 text-xs"
                         data-testid="enq-type-autoreply-subject" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest">Email body (HTML)</Label>
                  <Textarea rows={4} value={form.auto_reply.email_html}
                             onChange={(e) => setForm({ ...form, auto_reply: { ...form.auto_reply, email_html: e.target.value } })}
                             placeholder="<p>Hi {{name}},</p><p>Thanks for reaching out to {{studio}}. We'll respond within 24 hours…</p>"
                             className="rounded-none text-xs font-mono"
                             data-testid="enq-type-autoreply-html" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest">WhatsApp text</Label>
                  <Textarea rows={2} value={form.auto_reply.whatsapp_text}
                             onChange={(e) => setForm({ ...form, auto_reply: { ...form.auto_reply, whatsapp_text: e.target.value } })}
                             placeholder="Hi {{name}}! We received your {{type}} enquiry ({{code}}). Team will be in touch shortly."
                             className="rounded-none text-xs"
                             data-testid="enq-type-autoreply-wa" />
                </div>
                {!isNew && (
                  <PreviewAutoReplyButton ws={ws} typeId={type.id}
                                           enabled={form.auto_reply.enabled} />
                )}
                {isNew && (
                  <div className="text-[10px] text-muted-foreground italic">
                    Save the type first, then re-open to send yourself a test.
                  </div>
                )}
              </div>
            )}
          </div>
          {/* Feb 2026 · R280.8 · Assignment auto-rule */}
          <AutoAssignPoolPicker ws={ws} form={form} setForm={setForm} />
          <div className="border border-border">
            <button type="button"
                     onClick={() => setShowPageConfig(!showPageConfig)}
                     className="w-full flex items-center justify-between p-3 hover:bg-muted transition-colors"
                     data-testid="enq-type-pageconfig-toggle">
              <span className="flex items-center gap-2">
                <Palette className="w-3.5 h-3.5 text-primary" />
                <span className="uppercase tracking-widest text-[10px] font-semibold">Public page look & feel</span>
              </span>
              <span className="text-[10px] text-muted-foreground">{showPageConfig ? "− Hide" : "+ Show"}</span>
            </button>
            {showPageConfig && (
              <div className="p-3 border-t border-border">
                <div className="text-[10px] text-muted-foreground mb-3 italic">
                  Applies to <code>/enquire/{"{wsId}"}/{form.slug || "<slug>"}</code>. Widgets can override with their own styling.
                </div>
                <PageStyleEditor ws={ws} style={form.page_config}
                                  onChange={(pc) => setForm({ ...form, page_config: pc })}
                                  testidPrefix="enq-type-page" />
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="p-4 border-t border-border sticky bottom-0 bg-background">
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={busy} data-testid="enq-type-save">
            {busy ? "Saving…" : (isNew ? "Create type" : "Save changes")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PreviewAutoReplyButton({ ws, typeId, enabled }) {
  const [busy, setBusy] = useState(false);
  const [toEmail, setToEmail] = useState("");
  const [toPhone, setToPhone] = useState("");
  const send = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (toEmail) params.set("to_email", toEmail);
      if (toPhone) params.set("to_phone", toPhone);
      const r = await api.post(`/workspaces/${ws}/enquiries/types/${typeId}/preview-reply?${params.toString()}`);
      const sent = r.data?.sent_to || {};
      const parts = [];
      if (sent.email) parts.push(`email to ${sent.email}`);
      if (sent.phone) parts.push(`WhatsApp to ${sent.phone}`);
      toast.success(parts.length ? `Test sent — ${parts.join(" + ")}` : "Test triggered");
    } catch (e) { toast.error(apiErrorMessage(e, "Test failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="border-t border-border pt-3 mt-2 space-y-2" data-testid="enq-type-autoreply-preview">
      <div className="text-[10px] uppercase tracking-widest text-amber-800 font-semibold">Send me a test</div>
      <div className="grid grid-cols-2 gap-2">
        <Input value={toEmail} onChange={(e) => setToEmail(e.target.value)}
                placeholder="Override email (blank = your account email)"
                className="rounded-none h-8 text-xs"
                data-testid="enq-type-autoreply-preview-email" />
        <Input value={toPhone} onChange={(e) => setToPhone(e.target.value)}
                placeholder="Override phone (blank = your account phone)"
                className="rounded-none h-8 text-xs"
                data-testid="enq-type-autoreply-preview-phone" />
      </div>
      <Button size="sm" variant="outline" className="rounded-none text-[10px] uppercase tracking-widest"
               onClick={send} disabled={busy || !enabled}
               data-testid="enq-type-autoreply-preview-btn">
        {busy ? "Sending…" : "Send test to me"}
      </Button>
      <div className="text-[10px] text-muted-foreground">
        Uses the currently saved auto-reply for this type. Save any edits before testing.
      </div>
    </div>
  );
}


function NewEnquiryDialog({ ws, types, members, onClose, onCreated }) {
  const activeTypes = (types || []).filter((t) => t.active !== false);
  const [form, setForm] = useState({
    type_id: activeTypes[0]?.id || "",
    subtype_slug: "",
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    company: "",
    budget: "",
    message: "",
    assigned_to: "",
    create_lead: false,
    lead_id: "",         // set when admin picks an existing matched lead
  });
  const [busy, setBusy] = useState(false);
  const [matches, setMatches] = useState({ leads: [], open_enquiries: [] });
  const [ignoreDupe, setIgnoreDupe] = useState(false);
  const type = activeTypes.find((t) => t.id === form.type_id) || {};

  // Debounced lookup: when email or phone changes, hit /enquiries/match
  useEffect(() => {
    const email = form.customer_email.trim();
    const phone = form.customer_phone.trim();
    if (!email && !phone) { setMatches({ leads: [], open_enquiries: [] }); return; }
    const h = setTimeout(async () => {
      try {
        const p = new URLSearchParams();
        if (email) p.set("email", email);
        if (phone) p.set("phone", phone);
        const r = await api.get(`/workspaces/${ws}/enquiries/match?${p.toString()}`);
        setMatches(r.data || { leads: [], open_enquiries: [] });
      } catch { /* silent */ }
    }, 400);
    return () => clearTimeout(h);
  }, [ws, form.customer_email, form.customer_phone]);

  const hasOpenDupe = matches.open_enquiries?.length > 0;
  const linkedLead = matches.leads.find((l) => l.id === form.lead_id);

  const submit = async () => {
    if (!form.customer_name.trim()) { toast.error("Customer name is required"); return; }
    if (!form.type_id) { toast.error("Pick an enquiry type"); return; }
    if (hasOpenDupe && !ignoreDupe) {
      toast.error("This customer already has an open enquiry — link that one or tick 'Create anyway'.");
      return;
    }
    setBusy(true);
    try {
      const body = {
        type_id: form.type_id,
        subtype_slug: form.subtype_slug || null,
        customer_name: form.customer_name.trim(),
        customer_email: form.customer_email.trim() || null,
        customer_phone: form.customer_phone.trim() || null,
        company: form.company.trim(),
        budget: form.budget.trim(),
        message: form.message.trim(),
        source: "manual",
        // Link to picked lead outright; else fall back to create_lead flag
        lead_id: form.lead_id || null,
        create_lead: !form.lead_id && form.create_lead,
      };
      const r = await api.post(`/workspaces/${ws}/enquiries`, body);
      const created = r.data?.enquiry;
      // If admin picked a specific assignee, overwrite round-robin choice
      if (created?.id && form.assigned_to && form.assigned_to !== created.assigned_to) {
        try {
          await api.patch(`/workspaces/${ws}/enquiries/${created.id}`, {
            assigned_to: form.assigned_to,
          });
        } catch { /* non-fatal */ }
      }
      toast.success(`Enquiry ${created?.code || "created"}`);
      onCreated();
    } catch (e) { toast.error(apiErrorMessage(e, "Create failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-none max-w-lg w-[calc(100vw-1rem)] max-h-[92vh] overflow-y-auto p-0" data-testid="enq-new-dialog">
        <DialogHeader className="p-4 border-b border-border sticky top-0 bg-background z-10">
          <DialogTitle>New enquiry</DialogTitle>
        </DialogHeader>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Type *</Label>
              <select value={form.type_id}
                      onChange={(e) => setForm({ ...form, type_id: e.target.value, subtype_slug: "" })}
                      className="w-full h-9 border border-border bg-background text-xs rounded-none px-2"
                      data-testid="enq-new-type">
                <option value="">Pick a type</option>
                {activeTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Subtype</Label>
              <select value={form.subtype_slug}
                      onChange={(e) => setForm({ ...form, subtype_slug: e.target.value })}
                      className="w-full h-9 border border-border bg-background text-xs rounded-none px-2"
                      data-testid="enq-new-subtype">
                <option value="">—</option>
                {(type.subtypes || []).map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Customer name *</Label>
            <Input value={form.customer_name}
                    onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                    className="rounded-none h-9" data-testid="enq-new-name" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Email</Label>
              <Input type="email" value={form.customer_email}
                      onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                      className="rounded-none h-9" data-testid="enq-new-email" />
            </div>
            <div>
              <Label className="text-xs">Phone</Label>
              <Input value={form.customer_phone}
                      onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                      className="rounded-none h-9" data-testid="enq-new-phone" />
            </div>
          </div>
          <MatchPanel matches={matches}
                       pickedLeadId={form.lead_id}
                       onPickLead={(id) => setForm({ ...form, lead_id: id })}
                       ignoreDupe={ignoreDupe}
                       setIgnoreDupe={setIgnoreDupe} />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Company</Label>
              <Input value={form.company}
                      onChange={(e) => setForm({ ...form, company: e.target.value })}
                      className="rounded-none h-9" data-testid="enq-new-company" />
            </div>
            <div>
              <Label className="text-xs">Budget</Label>
              <Input value={form.budget}
                      onChange={(e) => setForm({ ...form, budget: e.target.value })}
                      placeholder="₹ 50k / 5 lakh / TBD"
                      className="rounded-none h-9" data-testid="enq-new-budget" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Message / brief</Label>
            <Textarea rows={3} value={form.message}
                       onChange={(e) => setForm({ ...form, message: e.target.value })}
                       placeholder="What are they asking for?"
                       className="rounded-none text-xs" data-testid="enq-new-message" />
          </div>
          <div>
            <Label className="text-xs">Assign to (optional)</Label>
            <select value={form.assigned_to}
                    onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}
                    className="w-full h-9 border border-border bg-background text-xs rounded-none px-2"
                    data-testid="enq-new-assign">
              <option value="">Round-robin (auto)</option>
              {members.map((m) => {
                const uid = m.user_id || m.id;
                return <option key={uid} value={uid}>{m.name || m.email}</option>;
              })}
            </select>
          </div>
          {!form.lead_id && (
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={form.create_lead}
                      onChange={(e) => setForm({ ...form, create_lead: e.target.checked })}
                      data-testid="enq-new-create-lead" />
              Also create a new lead in CRM
            </label>
          )}
          {form.lead_id && linkedLead && (
            <div className="text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-300 px-2 py-1.5"
                 data-testid="enq-new-lead-linked">
              → Will link this enquiry to lead <b>{linkedLead.name}</b>
              {linkedLead.enquiry_count > 0 && (
                <span className="text-[10px] text-emerald-700"> · {linkedLead.enquiry_count} previous enquir{linkedLead.enquiry_count === 1 ? "y" : "ies"}</span>
              )}
            </div>
          )}
        </div>
        <DialogFooter className="p-4 border-t border-border sticky bottom-0 bg-background">
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="enq-new-save">
            {busy ? "Creating…" : "Create enquiry"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function MatchPanel({ matches, pickedLeadId, onPickLead, ignoreDupe, setIgnoreDupe }) {
  const leads = matches?.leads || [];
  const openEnq = matches?.open_enquiries || [];
  if (!leads.length && !openEnq.length) return null;
  return (
    <div className="space-y-2" data-testid="enq-match-panel">
      {openEnq.length > 0 && (
        <div className="border border-amber-400 bg-amber-50 p-2.5" data-testid="enq-match-dupe">
          <div className="text-[10px] uppercase tracking-widest font-bold text-amber-900 mb-1">
            ⚠ Duplicate check — {openEnq.length} open enquir{openEnq.length === 1 ? "y" : "ies"} already
          </div>
          <ul className="space-y-1 text-[11px] max-h-24 overflow-y-auto">
            {openEnq.map((e) => (
              <li key={e.id} className="flex items-center gap-2">
                <span className="font-mono text-[10px] bg-white border border-amber-300 px-1">{e.code}</span>
                <span className="truncate flex-1">{e.customer_name} · {e.type_name}</span>
                <span className="text-[10px] text-amber-800 uppercase tracking-widest">{e.status}</span>
              </li>
            ))}
          </ul>
          <label className="flex items-center gap-2 text-[11px] mt-2 text-amber-900">
            <input type="checkbox" checked={ignoreDupe}
                    onChange={(e) => setIgnoreDupe(e.target.checked)}
                    data-testid="enq-match-ignore-dupe" />
            Create anyway — this is a different request
          </label>
        </div>
      )}
      {leads.length > 0 && (
        <div className="border border-emerald-300 bg-emerald-50/60 p-2.5" data-testid="enq-match-leads">
          <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-900 mb-1">
            {leads.length} matching lead{leads.length === 1 ? "" : "s"} — link to attribute this enquiry
          </div>
          <div className="space-y-1">
            {leads.map((l) => {
              const picked = pickedLeadId === l.id;
              return (
                <label key={l.id}
                        className={`flex items-center gap-2 p-1.5 border cursor-pointer text-[11px] ${
                          picked ? "border-emerald-700 bg-white" : "border-emerald-200 hover:border-emerald-500 bg-white/60"
                        }`}
                        data-testid={`enq-match-lead-${l.id}`}>
                  <input type="radio" name="matched-lead" checked={picked}
                          onChange={() => onPickLead(picked ? "" : l.id)} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{l.name}</div>
                    <div className="text-[10px] text-muted-foreground truncate">
                      {l.email || "—"} · {l.phone || "—"} {l.company ? `· ${l.company}` : ""}
                    </div>
                  </div>
                  <span className="text-[10px] font-mono bg-emerald-100 border border-emerald-300 px-1 whitespace-nowrap">
                    {l.enquiry_count || 0} enq
                  </span>
                </label>
              );
            })}
          </div>
          {pickedLeadId && (
            <button type="button" onClick={() => onPickLead("")}
                     className="mt-1.5 text-[10px] uppercase tracking-widest text-emerald-800 underline"
                     data-testid="enq-match-lead-clear">
              Unlink lead
            </button>
          )}
        </div>
      )}
    </div>
  );
}



// ==============================================================
// Feb 2026 · Enquiry Widgets (embeddable customer-facing widgets)
// ==============================================================
function Widgets({ ws }) {
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // widget | "new" | null
  const publicOrigin = window.location.origin;

  const load = async () => {
    setLoading(true);
    try {
      const [w, t] = await Promise.all([
        api.get(`/workspaces/${ws}/enquiry-widgets`),
        api.get(`/workspaces/${ws}/enquiries/types`),
      ]);
      setRows(w.data?.widgets || []);
      setTypes(t.data?.types || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
    setLoading(false);
  };
  useEffect(() => { if (ws) load(); /* eslint-disable-next-line */ }, [ws]);

  const remove = async (widget) => {
    if (!window.confirm(`Delete "${widget.name}"? Its embed URL will stop working.`)) return;
    try {
      await api.delete(`/workspaces/${ws}/enquiry-widgets/${widget.id}`);
      toast.success("Widget deleted");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copied`); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <div className="space-y-4" data-testid="enq-widgets-tab">
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
          {rows.length} widget{rows.length === 1 ? "" : "s"}
        </div>
        <Button size="sm" className="rounded-none ml-auto" onClick={() => setEditing("new")} data-testid="enq-widget-new">
          <Plus className="w-3.5 h-3.5 mr-1" /> New widget
        </Button>
      </div>
      {loading ? (
        <div className="text-xs text-muted-foreground italic">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-border p-8 text-center">
          <Palette className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
          <div className="text-sm font-semibold mb-1">No widgets yet</div>
          <div className="text-xs text-muted-foreground mb-3">Wrap one or more enquiry types into a styled embeddable page.</div>
          <Button size="sm" className="rounded-none" onClick={() => setEditing("new")}>
            Create first widget
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {rows.map((w) => {
            const publicUrl = `${publicOrigin}/enquire-w/${w.slug}`;
            const iframe = `<iframe src="${publicUrl}" style="width:100%;min-height:720px;border:0" title="${w.name}"></iframe>`;
            const wTypes = (w.type_ids || []).map((id) => types.find((t) => t.id === id)?.name).filter(Boolean);
            return (
              <div key={w.id} className="border border-border p-3 space-y-2 bg-background" data-testid={`enq-widget-row-${w.id}`}>
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold truncate">{w.name}</div>
                    <div className="text-[10px] text-muted-foreground truncate">/{w.slug}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5 truncate">
                      {wTypes.length ? wTypes.join(" · ") : "(no types)"}
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-[10px] text-muted-foreground">
                      <span>👁 {w.views || 0}</span>
                      <span>✉ {w.submissions || 0}</span>
                      {!w.active && <span className="text-rose-700 font-semibold">INACTIVE</span>}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <button onClick={() => setEditing(w)} className="p-1.5 hover:bg-muted" title="Edit"
                             data-testid={`enq-widget-edit-${w.id}`}>
                      <Settings className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => remove(w)} className="p-1.5 hover:bg-rose-100 text-rose-700" title="Delete"
                             data-testid={`enq-widget-delete-${w.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-1.5 text-[11px]">
                  <div className="flex items-center gap-1.5">
                    <a href={publicUrl} target="_blank" rel="noreferrer"
                        className="flex-1 truncate underline text-primary"
                        data-testid={`enq-widget-link-${w.id}`}>{publicUrl}</a>
                    <button onClick={() => copy(publicUrl, "Link")} className="p-1 hover:bg-muted"
                             data-testid={`enq-widget-copy-link-${w.id}`}>
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>
                  <button onClick={() => copy(iframe, "Iframe snippet")}
                           className="text-left text-[10px] text-muted-foreground border border-border bg-muted/30 p-1.5 hover:bg-muted font-mono truncate"
                           data-testid={`enq-widget-copy-iframe-${w.id}`}>
                    {iframe}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {editing && (
        <WidgetDialog ws={ws} widget={editing === "new" ? null : editing} types={types}
                       onClose={() => setEditing(null)}
                       onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}


function WidgetDialog({ ws, widget, types, onClose, onSaved }) {
  const isNew = !widget;
  const [form, setForm] = useState({
    name: widget?.name || "",
    slug: widget?.slug || "",
    type_ids: widget?.type_ids || [],
    active: widget?.active !== false,
    style: {
      hero_image_url: "", logo_url: "", tagline: "", welcome_html: "",
      thank_you_html: "", terms_html: "", cta_label: "Submit enquiry",
      primary_color: "#B8860B", accent_color: "#241A11",
      background_color: "#FBF7F0", text_color: "#241A11",
      font_family: "serif",
      require_phone: false, require_company: false, require_budget: false,
      require_message: true, show_workspace_name: true, show_powered_by: true,
      hide_hero: false,
      ...(widget?.style || {}),
    },
  });
  const [busy, setBusy] = useState(false);
  const activeTypes = types.filter((t) => t.active !== false);

  const toggleType = (id) => {
    setForm((f) => ({ ...f, type_ids: f.type_ids.includes(id)
      ? f.type_ids.filter((x) => x !== id)
      : [...f.type_ids, id] }));
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (form.type_ids.length === 0) { toast.error("Pick at least one enquiry type"); return; }
    setBusy(true);
    try {
      const body = { name: form.name.trim(), slug: form.slug.trim() || null,
                       type_ids: form.type_ids, active: form.active, style: form.style };
      if (isNew) await api.post(`/workspaces/${ws}/enquiry-widgets`, body);
      else await api.patch(`/workspaces/${ws}/enquiry-widgets/${widget.id}`, body);
      toast.success(isNew ? "Widget created" : "Widget saved");
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-none max-w-3xl w-[calc(100vw-1rem)] max-h-[92vh] overflow-y-auto p-0" data-testid="enq-widget-dialog">
        <DialogHeader className="p-4 border-b border-border sticky top-0 bg-background z-10">
          <DialogTitle>{isNew ? "New widget" : `Edit · ${widget.name}`}</DialogTitle>
        </DialogHeader>
        <div className="p-4 space-y-4">
          <section className="space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Basics</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="rounded-none h-9" data-testid="enq-widget-name" />
              </div>
              <div>
                <Label className="text-xs">Slug (URL)</Label>
                <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })}
                        placeholder="auto from name" className="rounded-none h-9"
                        data-testid="enq-widget-slug" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={form.active}
                      onChange={(e) => setForm({ ...form, active: e.target.checked })}
                      data-testid="enq-widget-active" />
              Active (embed URL live)
            </label>
          </section>
          <section className="space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
              Enquiry types (pick 1 or many) — {form.type_ids.length} selected
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {activeTypes.map((t) => (
                <label key={t.id} className={`flex items-center gap-2 p-2 border cursor-pointer text-xs ${
                  form.type_ids.includes(t.id) ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                }`} data-testid={`enq-widget-type-${t.id}`}>
                  <input type="checkbox" checked={form.type_ids.includes(t.id)} onChange={() => toggleType(t.id)} />
                  <span className="flex-1">{t.name}</span>
                  <span className="text-[10px] text-muted-foreground">/{t.slug}</span>
                </label>
              ))}
            </div>
          </section>
          <PageStyleEditor ws={ws} style={form.style}
                            onChange={(s) => setForm({ ...form, style: s })}
                            testidPrefix="enq-widget" />
        </div>
        <DialogFooter className="p-4 border-t border-border sticky bottom-0 bg-background">
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={busy} data-testid="enq-widget-save">
            {busy ? "Saving…" : (isNew ? "Create widget" : "Save changes")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Shared style editor used by both WidgetDialog and per-type TypeDialog
function PageStyleEditor({ ws, style, onChange, testidPrefix = "page-style" }) {
  const set = (k, v) => onChange({ ...style, [k]: v });
  const [uploadBusy, setUploadBusy] = useState({ hero: false, logo: false });

  const upload = async (purpose, file) => {
    if (!file) return;
    setUploadBusy((b) => ({ ...b, [purpose]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/workspaces/${ws}/enquiries/upload-brand-image?purpose=${purpose}`, fd,
                                 { headers: { "Content-Type": "multipart/form-data" } });
      if (purpose === "hero") set("hero_image_url", r.data.url);
      else if (purpose === "logo") set("logo_url", r.data.url);
      toast.success(`${purpose === "hero" ? "Hero" : "Logo"} uploaded`);
    } catch (e) { toast.error(apiErrorMessage(e, "Upload failed")); }
    finally { setUploadBusy((b) => ({ ...b, [purpose]: false })); }
  };

  return (
    <section className="space-y-3" data-testid={`${testidPrefix}-style`}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Look & feel</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Hero image</Label>
          {style.hero_image_url && (
            <img src={_abs(style.hero_image_url)} alt="" className="w-full h-24 object-cover border border-border" />
          )}
          <div className="flex items-center gap-1.5">
            <label className="flex-1 flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-widest border border-border py-1.5 cursor-pointer hover:bg-muted"
                    data-testid={`${testidPrefix}-hero-upload`}>
              <UploadIcon className="w-3 h-3" />
              {uploadBusy.hero ? "Uploading…" : (style.hero_image_url ? "Replace" : "Upload")}
              <input type="file" accept="image/*" className="hidden"
                      onChange={(e) => upload("hero", e.target.files?.[0])} />
            </label>
            {style.hero_image_url && (
              <button onClick={() => set("hero_image_url", "")}
                       className="p-1.5 border border-border hover:bg-rose-50 text-rose-700"
                       data-testid={`${testidPrefix}-hero-clear`}>
                <Trash2 className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Logo</Label>
          {style.logo_url && (
            <img src={_abs(style.logo_url)} alt="" className="w-full h-24 object-contain border border-border bg-white" />
          )}
          <div className="flex items-center gap-1.5">
            <label className="flex-1 flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-widest border border-border py-1.5 cursor-pointer hover:bg-muted"
                    data-testid={`${testidPrefix}-logo-upload`}>
              <UploadIcon className="w-3 h-3" />
              {uploadBusy.logo ? "Uploading…" : (style.logo_url ? "Replace" : "Upload")}
              <input type="file" accept="image/*" className="hidden"
                      onChange={(e) => upload("logo", e.target.files?.[0])} />
            </label>
            {style.logo_url && (
              <button onClick={() => set("logo_url", "")}
                       className="p-1.5 border border-border hover:bg-rose-50 text-rose-700"
                       data-testid={`${testidPrefix}-logo-clear`}>
                <Trash2 className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      </div>
      <div>
        <Label className="text-xs">Tagline</Label>
        <Input value={style.tagline || ""} onChange={(e) => set("tagline", e.target.value)}
                placeholder="Under the hero title (short)"
                className="rounded-none h-9" data-testid={`${testidPrefix}-tagline`} />
      </div>
      <div>
        <Label className="text-xs">CTA button label</Label>
        <Input value={style.cta_label || ""} onChange={(e) => set("cta_label", e.target.value)}
                placeholder="Submit enquiry" className="rounded-none h-9"
                data-testid={`${testidPrefix}-cta`} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[
          ["primary_color", "Primary"],
          ["accent_color", "Accent"],
          ["background_color", "Background"],
          ["text_color", "Text"],
        ].map(([k, label]) => (
          <div key={k}>
            <Label className="text-[10px]">{label}</Label>
            <div className="flex items-center gap-1">
              <input type="color" value={style[k] || "#000000"} onChange={(e) => set(k, e.target.value)}
                      className="w-9 h-9 border border-border" data-testid={`${testidPrefix}-color-${k}`} />
              <Input value={style[k] || ""} onChange={(e) => set(k, e.target.value)}
                      className="rounded-none h-9 text-[10px] font-mono" />
            </div>
          </div>
        ))}
      </div>
      <div>
        <Label className="text-xs">Font family</Label>
        <select value={style.font_family || "serif"} onChange={(e) => set("font_family", e.target.value)}
                className="w-full h-9 border border-border bg-background text-xs rounded-none px-2"
                data-testid={`${testidPrefix}-font`}>
          <option value="serif">Serif (Cormorant Garamond)</option>
          <option value="sans">Sans (Inter / system)</option>
          <option value="mono">Mono</option>
        </select>
      </div>
      <div>
        <Label className="text-xs">Welcome HTML (above the form)</Label>
        <Textarea rows={3} value={style.welcome_html || ""} onChange={(e) => set("welcome_html", e.target.value)}
                   placeholder="<p>Tell us a bit about your requirement…</p>"
                   className="rounded-none text-xs font-mono" data-testid={`${testidPrefix}-welcome`} />
      </div>
      <div>
        <Label className="text-xs">Terms HTML (below the form)</Label>
        <Textarea rows={2} value={style.terms_html || ""} onChange={(e) => set("terms_html", e.target.value)}
                   placeholder="<small>By submitting you agree to our <a href='...'>terms</a>.</small>"
                   className="rounded-none text-xs font-mono" data-testid={`${testidPrefix}-terms`} />
      </div>
      <div>
        <Label className="text-xs">Thank-you HTML</Label>
        <Textarea rows={3} value={style.thank_you_html || ""} onChange={(e) => set("thank_you_html", e.target.value)}
                   placeholder="<p>Thanks — we'll be in touch within 24 hours.</p>"
                   className="rounded-none text-xs font-mono" data-testid={`${testidPrefix}-thankyou`} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-xs">
        {[
          ["hide_hero", "Hide hero (compact embed)"],
          ["require_phone", "Require phone"],
          ["require_company", "Require company"],
          ["require_budget", "Require budget"],
          ["require_message", "Require message"],
          ["show_workspace_name", "Show workspace name"],
          ["show_powered_by", "Show 'Powered by' footer"],
        ].map(([k, label]) => (
          <label key={k} className="flex items-center gap-2">
            <input type="checkbox" checked={!!style[k]} onChange={(e) => set(k, e.target.checked)}
                    data-testid={`${testidPrefix}-toggle-${k}`} />
            {label}
          </label>
        ))}
      </div>
    </section>
  );
}


// Resolve relative /api URLs against the backend origin so previews
// work in the admin dialog even before the widget goes live.
function _abs(url) {
  if (!url) return "";
  if (/^https?:\/\//.test(url)) return url;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}${url}`;
}



// ─── Feb 2026 · R280.8 · SLA response-time chip ──────────────────────
// Live "Responded in Xh" / "Pending Xh" indicator computed from
// `created_at` + `first_response_at` + `status`.
//   • Responded within 2h → emerald (fast)
//   • Responded 2–8h      → amber (fine)
//   • Responded >8h       → rose (slow — surfaces stale threads)
//   • No response yet     → pulsing amber ticking counter
// Terminal statuses (won / lost / cancelled) render a muted "—" so
// closed threads don't clutter the ops view.
const TERMINAL = new Set(["won", "lost", "cancelled", "closed", "converted"]);

function _sinceHours(iso) {
  if (!iso) return null;
  try {
    const ms = Date.now() - new Date(iso).getTime();
    return Math.max(0, ms / 3_600_000);
  } catch { return null; }
}

function _fmtHours(h) {
  if (h == null) return "—";
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 48) return `${Math.round(h * 10) / 10}h`;
  return `${Math.round(h / 24)}d`;
}

function SlaChip({ enquiry }) {
  const [nowTick, setNowTick] = useState(0);
  useEffect(() => {
    if (enquiry.first_response_at || TERMINAL.has(enquiry.status)) return;
    const t = setInterval(() => setNowTick((n) => n + 1), 60000);
    return () => clearInterval(t);
  }, [enquiry.first_response_at, enquiry.status]);
  // Force re-eval on tick.
  void nowTick;
  if (TERMINAL.has(enquiry.status)) {
    return <span className="text-[10px] text-muted-foreground">—</span>;
  }
  const created = enquiry.created_at;
  const responded = enquiry.first_response_at;
  const respondedIn = responded ? _sinceHours(created) - _sinceHours(responded) : null;
  const pendingFor = responded ? null : _sinceHours(created);
  if (respondedIn != null) {
    const hrs = respondedIn;
    const tone = hrs <= 2
      ? "bg-emerald-50 border-emerald-300 text-emerald-800"
      : hrs <= 8
        ? "bg-amber-50 border-amber-300 text-amber-800"
        : "bg-rose-50 border-rose-300 text-rose-800";
    return (
      <span className={`text-[10px] px-1.5 py-0.5 border font-mono whitespace-nowrap ${tone}`}
              title={`First response ${_fmtHours(hrs)} after enquiry landed`}
              data-testid={`enq-sla-${enquiry.id}`}>
        ✓ {_fmtHours(hrs)}
      </span>
    );
  }
  // No response yet — pulsing pending badge.
  const pf = pendingFor || 0;
  const tone = pf <= 2
    ? "bg-amber-50 border-amber-300 text-amber-800"
    : pf <= 8
      ? "bg-orange-50 border-orange-400 text-orange-800"
      : "bg-rose-50 border-rose-400 text-rose-800";
  return (
    <span className={`text-[10px] px-1.5 py-0.5 border font-mono whitespace-nowrap animate-pulse ${tone}`}
            title={`Waiting on first response · ${_fmtHours(pf)} elapsed`}
            data-testid={`enq-sla-${enquiry.id}`}>
      ⌛ {_fmtHours(pf)}
    </span>
  );
}




// ─── Feb 2026 · R280.8 · Auto-assign shortlist picker ────────────────
// Renders inside TypeDialog. Admin picks the users who should share
// this enquiry type via round-robin (default) or a fixed "first" rule.
// Pool empty → falls back to the workspace-wide `enquiry.manage` default.
function AutoAssignPoolPicker({ ws, form, setForm }) {
  const [members, setMembers] = useState([]);
  useEffect(() => {
    if (!ws) return;
    api.get(`/workspaces/${ws}/members`)
      .then((r) => setMembers(Array.isArray(r.data) ? r.data : []))
      .catch(() => setMembers([]));
  }, [ws]);
  const pool = form.auto_assign_pool || [];
  const inPool = new Set(pool);
  const toggle = (uid) => {
    setForm({
      ...form,
      auto_assign_pool: inPool.has(uid) ? pool.filter((u) => u !== uid) : [...pool, uid],
    });
  };
  return (
    <div className="border border-border p-3 space-y-3" data-testid="enq-type-auto-assign">
      <div className="flex items-center gap-2">
        <Users className="w-3.5 h-3.5 text-primary" />
        <span className="uppercase tracking-widest text-[10px] font-semibold">Auto-assign new enquiries</span>
      </div>
      <p className="text-[11px] text-muted-foreground">Pick a shortlist for this type. Blank list = default (round-robin across every team member with <code>enquiry.manage</code>).</p>
      <div className="flex flex-wrap gap-1.5" data-testid="enq-type-auto-assign-pool">
        {members.length === 0 && (
          <span className="text-[10px] text-muted-foreground italic">Loading team…</span>
        )}
        {members.map((m) => {
          const on = inPool.has(m.user_id);
          const name = m.name || m.email || m.user_id;
          return (
            <button key={m.user_id} type="button" onClick={() => toggle(m.user_id)}
                    data-testid={`enq-type-auto-assign-toggle-${m.user_id}`}
                    className={`text-[11px] px-2 py-1 border transition-colors ${on
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-white text-foreground border-border hover:bg-muted"}`}>
              {on && "✓ "}{name}
            </button>
          );
        })}
      </div>
      {pool.length > 0 && (
        <div className="flex items-center gap-3 text-[11px]">
          <div className="uppercase tracking-widest text-[10px] font-semibold text-muted-foreground">Mode</div>
          <label className="inline-flex items-center gap-1.5">
            <input type="radio" name="auto_assign_mode" value="round_robin"
                     checked={form.auto_assign_mode === "round_robin"}
                     onChange={() => setForm({ ...form, auto_assign_mode: "round_robin" })}
                     data-testid="enq-type-auto-assign-mode-rr" />
            Round-robin
          </label>
          <label className="inline-flex items-center gap-1.5">
            <input type="radio" name="auto_assign_mode" value="first"
                     checked={form.auto_assign_mode === "first"}
                     onChange={() => setForm({ ...form, auto_assign_mode: "first" })}
                     data-testid="enq-type-auto-assign-mode-first" />
            Always first
          </label>
        </div>
      )}
    </div>
  );
}
